import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

const LOGO_SRC = require('@/assets/logo/favicon.png')

const PAGE = {
  width: 297,
  marginH: 8,
  marginTop: 18,
  marginBottom: 14
}

PAGE.tableWidth = PAGE.width - PAGE.marginH * 2 // 281mm

const PDF_CONFIG = {
  orientation: 'landscape',
  unit: 'mm',
  format: 'a4',
  margin: {
    top: PAGE.marginTop,
    right: PAGE.marginH,
    left: PAGE.marginH,
    bottom: PAGE.marginBottom
  },
  fontSize: 7,
  headerFontSize: 7,
  letterhead: {
    logo: { x: PAGE.marginH, y: 8, width: 16, height: 16 },
    titleY: 14,
    addressY: 21,
    lineY: 25,
    bottomY: 28
  },
  // Total = 281mm (11 kolom)
  columnWidths: {
    opd: 65,
    anggaran: 27,
    percent: 11,
    kas: 27,
    sp2dPagu: 24,
    sp2dRp: 27,
    sp2dAnggaran: 24
  }
}

const CENTER_COLUMNS = [8, 10]
const RIGHT_COLUMNS = [1, 2, 3, 7, 9]

const TABLE_HEAD = [
  [
    { content: 'Nama OPD', rowSpan: 2, styles: { valign: 'middle' } },
    { content: 'Anggaran Murni', rowSpan: 2, styles: { valign: 'middle' } },
    { content: 'Anggaran P1', rowSpan: 2, styles: { valign: 'middle' } },
    { content: 'Anggaran Perubahan', rowSpan: 2, styles: { valign: 'middle' } },
    { content: 'Perkembangan Fisik', colSpan: 3 },
    { content: 'Perkembangan Keuangan', colSpan: 4 }
  ],
  [
    'Realisasi\n(%)',
    'Target\n(%)',
    'Deviasi\n(%)',
    'Anggaran Kas\nPerbulan (Rp)',
    'Persentase SP2D\nterhadap Pagu\nAnggaran',
    'Realisasi Keuangan\n/ SP2D (Rp)',
    'Persentase SP2D\nterhadap Kas\nPerbulan'
  ]
]

function formatPdfCurrency (num) {
  const data = Number(num)
  if (isNaN(data)) return '0,00'
  return data.toLocaleString('id-ID', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

function formatPdfPercent (num) {
  if (num === '' || num === null || num === undefined) return '0'
  const data = Number(num)
  if (isNaN(data)) return String(num)
  return data.toLocaleString('id-ID', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

function normalizeList (data) {
  if (Array.isArray(data)) return data
  if (data && typeof data === 'object') return Object.values(data)
  return []
}

export function buildPdfExportRows ({ list, listMurni, detail, detailMurni }) {
  const normalizedList = normalizeList(list)
  const normalizedMurni = normalizeList(listMurni)

  const rows = normalizedList.map((item, index) => {
    const murniItem = normalizedMurni[index] || {}
    return [
      item.unit_name || '',
      formatPdfCurrency(murniItem.total_anggaran != null ? murniItem.total_anggaran : 0),
      formatPdfCurrency(item.total_anggaran_p1),
      formatPdfCurrency(item.total_anggaran_perubahan),
      formatPdfPercent(item.total_realisasi_fisik),
      formatPdfPercent(item.total_target_fisik),
      formatPdfPercent(item.deviasi),
      formatPdfCurrency(item.total_target_keu),
      formatPdfPercent(item.persentase_sp2d),
      formatPdfCurrency(item.total_realisasi_keu),
      formatPdfPercent(item.persentase_sp2d_anggaran)
    ]
  })

  if (detail && Object.keys(detail).length > 0) {
    rows.push([
      'JUMLAH',
      formatPdfCurrency(detailMurni && detailMurni.grand_total_anggaran != null ? detailMurni.grand_total_anggaran : 0),
      formatPdfCurrency(detail.grand_total_anggaran_p1),
      formatPdfCurrency(detail.grand_total_anggaran_perubahan),
      formatPdfPercent(detail.grand_total_realisasi_fisik),
      formatPdfPercent(detail.grand_total_total_target_fisik),
      formatPdfPercent(detail.grand_total_deviasi),
      formatPdfCurrency(detail.grand_total_target_keuangan),
      formatPdfPercent(detail.grand_total_persentase_sp2d),
      formatPdfCurrency(detail.total_realisasi_keuangan),
      formatPdfPercent(detail.grand_total_persentase_sp2d_anggaran)
    ])
  }

  return rows
}

function getColumnStyles () {
  const w = PDF_CONFIG.columnWidths
  return {
    0: { cellWidth: w.opd, halign: 'left' },
    1: { cellWidth: w.anggaran, halign: 'right' },
    2: { cellWidth: w.anggaran, halign: 'right' },
    3: { cellWidth: w.anggaran, halign: 'right' },
    4: { cellWidth: w.percent, halign: 'right' },
    5: { cellWidth: w.percent, halign: 'right' },
    6: { cellWidth: w.percent, halign: 'right' },
    7: { cellWidth: w.kas, halign: 'right' },
    8: { cellWidth: w.sp2dPagu, halign: 'center' },
    9: { cellWidth: w.sp2dRp, halign: 'right' },
    10: { cellWidth: w.sp2dAnggaran, halign: 'center' }
  }
}

function getBodyCellAlign (colIndex) {
  if (colIndex === 0) return 'left'
  if (CENTER_COLUMNS.includes(colIndex)) return 'center'
  if (RIGHT_COLUMNS.includes(colIndex)) return 'right'
  return 'left'
}

function drawLetterhead (doc) {
  const pageWidth = doc.internal.pageSize.getWidth()
  const { letterhead, margin } = PDF_CONFIG
  const centerX = pageWidth / 2
  const lineLeft = margin.left
  const lineRight = pageWidth - margin.right

  try {
    doc.addImage(
      LOGO_SRC,
      'PNG',
      letterhead.logo.x,
      letterhead.logo.y,
      letterhead.logo.width,
      letterhead.logo.height
    )
  } catch (error) {
    console.warn('Logo PDF tidak dapat dimuat:', error)
  }

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.text('PEMERINTAH KABUPATEN KLATEN', centerX, letterhead.titleY, { align: 'center' })

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.text(
    'Jl. Pemuda No. 294 Klaten 57424.. Telepon : 0272-321046 | Email : pemkab@klaten.go.id',
    centerX,
    letterhead.addressY,
    { align: 'center' }
  )

  doc.setDrawColor(0, 0, 0)
  doc.setLineWidth(0.4)
  doc.line(lineLeft, letterhead.lineY, lineRight, letterhead.lineY)
  doc.setLineWidth(0.8)
  doc.line(lineLeft, letterhead.lineY + 1.2, lineRight, letterhead.lineY + 1.2)

  return letterhead.bottomY
}

function drawReportTitle (doc, year, monthText, startY) {
  const pageWidth = doc.internal.pageSize.getWidth()
  const centerX = pageWidth / 2
  let y = startY + 7

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text('LAPORAN PERKEMBANGAN FISIK DAN KEUANGAN', centerX, y, { align: 'center' })
  y += 5.5

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.text(`TAHUN ANGGARAN ${year}`, centerX, y, { align: 'center' })
  y += 5
  doc.text(`SAMPAI DENGAN BULAN ${String(monthText).toUpperCase()}`, centerX, y, { align: 'center' })

  return y + 6
}

function drawPageNumbers (doc) {
  const totalPages = doc.internal.getNumberOfPages()
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7)
    doc.text(
      `Halaman ${i} dari ${totalPages}`,
      pageWidth / 2,
      pageHeight - 6,
      { align: 'center' }
    )
  }
}

export function generateRekapOpdPublicPdf ({
  fileName,
  year,
  monthText,
  list,
  listMurni,
  detail,
  detailMurni
}) {
  const doc = new jsPDF({
    orientation: PDF_CONFIG.orientation,
    unit: PDF_CONFIG.unit,
    format: PDF_CONFIG.format
  })

  const bodyRows = buildPdfExportRows({ list, listMurni, detail, detailMurni })
  const totalRowIndex = bodyRows.length - 1
  const hasTotalRow = detail && Object.keys(detail).length > 0

  let tableStartY = drawLetterhead(doc)
  tableStartY = drawReportTitle(doc, year, monthText, tableStartY)

  autoTable(doc, {
    startY: tableStartY,
    tableWidth: PAGE.tableWidth,
    head: TABLE_HEAD,
    body: bodyRows,
    theme: 'grid',
    styles: {
      fontSize: PDF_CONFIG.fontSize,
      cellPadding: 1.2,
      lineColor: [0, 0, 0],
      lineWidth: 0.15,
      overflow: 'hidden',
      valign: 'middle'
    },
    headStyles: {
      fillColor: [220, 220, 220],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      fontSize: PDF_CONFIG.headerFontSize,
      lineColor: [0, 0, 0],
      lineWidth: 0.3
    },
    bodyStyles: {
      lineColor: [0, 0, 0],
      lineWidth: 0.15
    },
    tableLineWidth: 0.4,
    tableLineColor: [0, 0, 0],
    columnStyles: getColumnStyles(),
    margin: PDF_CONFIG.margin,
    showHead: 'everyPage',
    rowPageBreak: 'avoid',
    didParseCell (data) {
      if (data.section === 'head') {
        data.cell.styles.lineWidth = 0.3
        data.cell.styles.fontStyle = 'bold'
        data.cell.styles.fillColor = [220, 220, 220]
        return
      }

      if (data.section === 'body') {
        data.cell.styles.halign = getBodyCellAlign(data.column.index)
        data.cell.styles.overflow = data.column.index === 0 ? 'linebreak' : 'hidden'

        if (hasTotalRow && data.row.index === totalRowIndex) {
          data.cell.styles.fontStyle = 'bold'
        }
      }
    }
  })

  drawPageNumbers(doc)
  doc.save(fileName)
}
